import { useState, useMemo, useCallback } from "react";
import { Plus, Edit2, Clock, Filter } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import ConfigDynamicTable from "@/components/BaseTable/ConfigDynamicTable";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import CustomButton from "@/components/common/Button";
import { notify } from "@/components/common/Notification";
import {
  DropdownService,
  SchemaService,
  DynamicModuleService,
} from "@/services/APIs";
import AddEmployeeRateModal from "./AddEmployeeRateModal";

const EMPLOYEE_RATE_SCHEMA_NAME = "PTS Employee Rate";
const EMPLOYEE_SCHEMA_NAME = "PTS Emp";
const DEPARTMENT_SCHEMA_NAME = "PTS Department";
const CURRENCY_SCHEMA_NAME = "PTS Currency";
const DESIGNATION_SCHEMA_NAME = "PTS Designation";

const EmployeeRatesPage = () => {
  const queryClient = useQueryClient();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string>("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRateId, setEditingRateId] = useState<string | null>(null);

  // Fetch dropdown data for Department
  const { data: departmentOptions } = useQuery({
    queryKey: ["department-dropdown", DEPARTMENT_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(DEPARTMENT_SCHEMA_NAME),
    enabled: !!DEPARTMENT_SCHEMA_NAME,
  });

  // Fetch dropdown data for Employees
  const { data: employeeOptions } = useQuery({
    queryKey: ["employee-dropdown", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(EMPLOYEE_SCHEMA_NAME),
    enabled: !!EMPLOYEE_SCHEMA_NAME,
  });

  // Fetch dropdown data for Currency
  const { data: currencyOptions } = useQuery({
    queryKey: ["currency-dropdown", CURRENCY_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(CURRENCY_SCHEMA_NAME),
    enabled: !!CURRENCY_SCHEMA_NAME,
  });

  // Fetch dropdown data for Designation
  const { data: designationOptions } = useQuery({
    queryKey: ["designation-dropdown", DESIGNATION_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(DESIGNATION_SCHEMA_NAME),
    enabled: !!DESIGNATION_SCHEMA_NAME,
  });

  // Get column grid data to generate all columns dynamically
  const { data: columnGridData } = useQuery({
    queryKey: ["columnGridData", EMPLOYEE_RATE_SCHEMA_NAME],
    queryFn: () => SchemaService.getColumnGridData(EMPLOYEE_RATE_SCHEMA_NAME),
  });

  // Fetch employee rates data for metrics
  const { data: ratesData } = useQuery({
    queryKey: ["moduleData", EMPLOYEE_RATE_SCHEMA_NAME],
    queryFn: () =>
      DynamicModuleService.getGridColItems(EMPLOYEE_RATE_SCHEMA_NAME, 1, 1000),
  });

  // Fetch employees data for metrics
  const { data: employeesData } = useQuery({
    queryKey: ["moduleData", EMPLOYEE_SCHEMA_NAME],
    queryFn: () =>
      DynamicModuleService.getGridColItems(EMPLOYEE_SCHEMA_NAME, 1, 1000),
  });

  // Calculate metrics
  const metrics = useMemo(() => {
    const totalEmployees =
      employeesData?.results?.length ?? employeesData?.length ?? 0;
    const rates = ratesData?.results ?? ratesData ?? [];
    const employeesWithRates = new Set(
      rates.map(
        (rate: any) => rate.employee ?? rate.employee_id ?? rate.pts_emp
      )
    ).size;
    const missingRates = totalEmployees - employeesWithRates;

    console.log(employeesData, "employeesData-----------");

    // Calculate average bill rate
    const billRates = rates
      .map((rate: any) => {
        const billRate = rate.bill_rate ?? rate.billRate ?? rate.billrate ?? 0;
        return typeof billRate === "string" ? parseFloat(billRate) : billRate;
      })
      .filter((rate: number) => !isNaN(rate) && rate > 0);

    const avgBillRate =
      billRates.length > 0
        ? billRates.reduce((sum: number, rate: number) => sum + rate, 0) /
          billRates.length
        : 0;

    // Calculate average margin
    const margins = rates
      .map((rate: any) => {
        const costRate = rate.cost_rate ?? rate.costRate ?? rate.costrate ?? 0;
        const billRate = rate.bill_rate ?? rate.billRate ?? rate.billrate ?? 0;
        const cost =
          typeof costRate === "string" ? parseFloat(costRate) : costRate;
        const bill =
          typeof billRate === "string" ? parseFloat(billRate) : billRate;
        if (bill > 0 && cost > 0) {
          return ((bill - cost) / bill) * 100;
        }
        return null;
      })
      .filter((margin: number | null) => margin !== null) as number[];

    const avgMargin =
      margins.length > 0
        ? margins.reduce((sum: number, margin: number) => sum + margin, 0) /
          margins.length
        : 0;

    return {
      totalEmployees,
      withRates: employeesWithRates,
      missingRates,
      avgBillRate: Math.round(avgBillRate),
      avgMargin: Math.round(avgMargin),
    };
  }, [employeesData, ratesData]);

  // Helper function to get employee name by ID
  const getEmployeeNameById = useCallback(
    (id: string) => {
      if (!id) {
        return null;
      }
      return employeeOptions?.find(
        (item: { value: string; label: string }) => item.value === id
      );
    },
    [employeeOptions]
  );

  // Helper function to get department name by ID
  const getDepartmentNameById = useCallback(
    (id: string) => {
      if (!id) {
        return null;
      }
      return departmentOptions?.find(
        (item: { value: string; label: string }) => item.value === id
      );
    },
    [departmentOptions]
  );

  // Helper function to get currency name by ID
  const getCurrencyNameById = useCallback(
    (id: string) => {
      if (!id) {
        return null;
      }
      return currencyOptions?.find(
        (item: { value: string; label: string }) => item.value === id
      );
    },
    [currencyOptions]
  );

  // Helper function to get designation name by ID
  const getDesignationNameById = useCallback(
    (id: string) => {
      if (!id) {
        return null;
      }
      return designationOptions?.find(
        (item: { value: string; label: string }) => item.value === id
      );
    },
    [designationOptions]
  );

  // Helper function to get employee data by ID
  const getEmployeeDataById = useCallback(
    (id: string) => {
      if (!id) {
        return null;
      }
      return (
        employeesData?.results?.find(
          (emp: any) => String(emp.id ?? emp.ID ?? emp._id) === String(id)
        ) ??
        employeesData?.find(
          (emp: any) => String(emp.id ?? emp.ID ?? emp._id) === String(id)
        )
      );
    },
    [employeesData]
  );

  // Generate columns dynamically
  const columns: ProColumns<any>[] = useMemo(() => {
    if (!columnGridData?.grid) {
      return [];
    }

    const dataColumns = columnGridData.grid.map((field: any) => {
      const fieldName = field.field_name.toLowerCase();
      const fieldTitle = field.field_title;

      const isEmployeeField =
        fieldName.includes("employee") ??
        fieldTitle?.toLowerCase()?.includes("employee") ??
        false;
      const isDepartmentField =
        fieldName.includes("department") ??
        fieldTitle?.toLowerCase()?.includes("department") ??
        false;
      const isCurrencyField =
        fieldName.includes("currency") ??
        fieldTitle?.toLowerCase()?.includes("currency") ??
        false;
      const isMarginField =
        fieldName.includes("margin") ??
        fieldTitle?.toLowerCase()?.includes("margin") ??
        false;
      const isCostRateField =
        fieldName.includes("cost") &&
        (fieldName.includes("rate") ?? fieldName.includes("cost"));
      const isBillRateField =
        fieldName.includes("bill") && fieldName.includes("rate");

      const baseColumn: ProColumns<any> = {
        title: fieldTitle,
        dataIndex: fieldName,
        key: fieldName,
      };

      // Render employee name
      if (isEmployeeField) {
        baseColumn.render = (_: any, entity: any) => {
          const value =
            entity?.[fieldName] ??
            entity?.employee ??
            entity?.employee_id ??
            entity?.pts_emp;
          if (!value) {
            return "-";
          }
          const employee = getEmployeeNameById(String(value));
          const employeeData =
            employeesData?.results?.find(
              (emp: any) =>
                String(emp.id ?? emp.ID ?? emp._id) === String(value)
            ) ??
            employeesData?.find(
              (emp: any) =>
                String(emp.id ?? emp.ID ?? emp._id) === String(value)
            );

          return (
            <div>
              <div className="font-semibold">
                {employee?.label ?? employeeData?.name ?? value}
              </div>
              {employeeData?.email && (
                <div className="text-xs text-gray-500">
                  {employeeData.email}
                </div>
              )}
            </div>
          );
        };
      }

      // Render department name
      if (isDepartmentField) {
        baseColumn.render = (_: any, entity: any) => {
          const value = entity?.[fieldName];
          if (!value) {
            return "-";
          }
          const department = getDepartmentNameById(String(value));
          return department?.label ?? value;
        };
      }

      // Render currency name
      if (isCurrencyField) {
        baseColumn.render = (_: any, entity: any) => {
          const value = entity?.[fieldName];
          if (!value) {
            return "-";
          }
          const currency = getCurrencyNameById(String(value));
          return currency?.label ?? value;
        };
      }

      // Render cost rate and bill rate with currency
      if (isCostRateField || isBillRateField) {
        baseColumn.render = (_: any, entity: any) => {
          const value = entity?.[fieldName];
          if (value === null || value === undefined || value === "") {
            return "-";
          }
          const numValue =
            typeof value === "string" ? parseFloat(value) : value;
          return `$${numValue.toFixed(2)}`;
        };
      }

      // Render margin as percentage with green pill
      if (isMarginField) {
        baseColumn.render = (_: any, entity: any) => {
          const value = entity?.[fieldName];
          if (value === null || value === undefined || value === "") {
            return "-";
          }
          const numValue =
            typeof value === "string" ? parseFloat(value) : value;
          const percentage = Math.round(numValue);
          return (
            <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
              {percentage}%
            </span>
          );
        };
      }

      // Render effective date
      if (fieldName.includes("effective") && fieldName.includes("from")) {
        baseColumn.render = (_: any, entity: any) => {
          const value = entity?.[fieldName];
          if (!value) {
            return "-";
          }
          // Format date to YYYY-MM-DD
          if (typeof value === "string") {
            if (value.includes("T")) {
              return value.split("T")[0];
            }
            return value;
          }
          return value;
        };
      }

      return baseColumn;
    });

    // Add Department and Designation columns after Employee column
    const employeeColumnIndex = dataColumns.findIndex((col: any) => {
      const fieldName = col.dataIndex?.toLowerCase() ?? "";
      return fieldName.includes("employee");
    });

    // Insert Department and Designation columns after Employee
    const columnsWithExtra: ProColumns<any>[] = [...dataColumns];

    if (employeeColumnIndex >= 0) {
      // Add Department column
      const employeeField = columnGridData.grid.find((field: any) => {
        const fieldName = field.field_name.toLowerCase();
        return fieldName.includes("employee");
      });

      if (employeeField) {
        const employeeFieldName = employeeField.field_name.toLowerCase();

        // Department column (virtual column - not in schema)
        columnsWithExtra.splice(employeeColumnIndex + 1, 0, {
          title: "Department",
          key: "employee_department",
          dataIndex: undefined, // No dataIndex to prevent API query
          hideInSearch: true,
          hideInSetting: false,
          render: (_: any, record: any) => {
            const employeeId =
              record?.[employeeFieldName] ??
              record?.employee ??
              record?.employee_id ??
              record?.pts_emp;
            if (!employeeId) {
              return "-";
            }
            const employeeData = getEmployeeDataById(String(employeeId));
            if (!employeeData) {
              return "-";
            }
            // Get department from employee data
            const deptField = Object.keys(employeeData).find(key =>
              key.toLowerCase().includes("department")
            );
            const deptId = deptField ? employeeData[deptField] : null;
            if (!deptId) {
              return "-";
            }
            const department = getDepartmentNameById(String(deptId));
            return department?.label ?? "-";
          },
        });

        // Designation column (virtual column - not in schema)
        columnsWithExtra.splice(employeeColumnIndex + 2, 0, {
          title: "Designation",
          key: "employee_designation",
          dataIndex: undefined, // No dataIndex to prevent API query
          hideInSearch: true,
          hideInSetting: false,
          render: (_: any, record: any) => {
            const employeeId =
              record?.[employeeFieldName] ??
              record?.employee ??
              record?.employee_id ??
              record?.pts_emp;
            if (!employeeId) {
              return "-";
            }
            const employeeData = getEmployeeDataById(String(employeeId));
            if (!employeeData) {
              return "-";
            }
            // Get designation from employee data
            const desigField = Object.keys(employeeData).find(key =>
              key.toLowerCase().includes("designation")
            );
            const desigId = desigField ? employeeData[desigField] : null;
            if (!desigId) {
              return "-";
            }
            const designation = getDesignationNameById(String(desigId));
            return designation?.label ?? "-";
          },
        });
      }
    }

    return [
      ...columnsWithExtra,
      {
        title: "Actions",
        width: 120,
        valueType: "option",
        key: "actions",
        fixed: "right",
        render: (_: any, record: any) => {
          const recordId =
            record?.id ?? record?.ID ?? record?.Id ?? record?._id;

          return [
            <CustomButton
              key="edit"
              icon={<Edit2 size={16} />}
              type="text"
              onClick={() => {
                setEditingRateId(recordId);
                setIsModalOpen(true);
              }}
              title="Edit Rate"
            />,
            <CustomButton
              key="history"
              icon={<Clock size={16} />}
              type="text"
              onClick={() => {
                // TODO: Implement history view
                notify.info("History view coming soon");
              }}
              title="View History"
            />,
          ];
        },
      },
    ];
  }, [
    columnGridData,
    employeesData,
    getCurrencyNameById,
    getDepartmentNameById,
    getDesignationNameById,
    getEmployeeDataById,
    getEmployeeNameById,
  ]);

  const handleAddRate = () => {
    setEditingRateId(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingRateId(null);
    queryClient.invalidateQueries({
      queryKey: ["moduleData", EMPLOYEE_RATE_SCHEMA_NAME],
    });
  };

  // Build filter based on selected department
  const filter = useMemo(() => {
    if (selectedDepartment === "all") {
      return {};
    }
    return { department: selectedDepartment };
  }, [selectedDepartment]);

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      {/* Page Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]">Employee Rates</h1>
          <p className="text-sm text-[#627384]">
            Manage billing and cost rates for employees
          </p>
        </div>
        <Button
          onClick={handleAddRate}
          className="bg-primary hover:bg-primary/90 flex items-center gap-2 text-white"
        >
          <Plus className="h-4 w-4" />
          Add Rate
        </Button>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Card className="border border-gray-200 bg-white">
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">Total Employees</p>
            <p className="text-primary text-2xl font-bold">
              {metrics.totalEmployees}
            </p>
          </CardContent>
        </Card>
        <Card className="border border-gray-200 bg-white">
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">With Rates</p>
            <p className="text-2xl font-bold text-green-600">
              {metrics.withRates}
            </p>
          </CardContent>
        </Card>
        <Card className="border border-gray-200 bg-white">
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">Missing Rates</p>
            <p className="text-2xl font-bold text-red-600">
              {metrics.missingRates}
            </p>
          </CardContent>
        </Card>
        <Card className="border border-gray-200 bg-white">
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">Avg Bill Rate</p>
            <p className="text-primary text-2xl font-bold">
              ${metrics.avgBillRate}/h
            </p>
          </CardContent>
        </Card>
        <Card className="border border-gray-200 bg-white">
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">Avg Margin</p>
            <p className="text-primary text-2xl font-bold">
              {metrics.avgMargin}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters Section */}
      <Card className="border border-gray-200 bg-white">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Filter className="h-5 w-5 text-gray-500" />
            <div className="flex-1">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Department
              </label>
              <Select
                value={selectedDepartment}
                onValueChange={setSelectedDepartment}
              >
                <SelectTrigger className="w-full sm:w-[250px]">
                  <SelectValue placeholder="All Departments" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Departments</SelectItem>
                  {departmentOptions?.map(
                    (option: { value: string; label: string }) => (
                      <SelectItem
                        key={option.value}
                        value={String(option.value)}
                      >
                        {option.label}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Employee Rate Configuration Table */}
      <Card className="border border-gray-200 bg-white">
        <CardContent className="p-4">
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-semibold text-[#111827]">
              Employee Rate Configuration
            </h2>
            <span className="text-sm text-gray-600">
              {metrics.totalEmployees} Employees
            </span>
          </div>
          <div className="overflow-x-auto">
            <ConfigDynamicTable
              tableName={EMPLOYEE_RATE_SCHEMA_NAME}
              columns={columns}
              headerTitle=""
              rowKey="id"
              filter={filter}
              selectedRowKeys={selectedRowKeys}
              setSelectedRowKeys={setSelectedRowKeys}
            />
          </div>
        </CardContent>
      </Card>

      {/* Add Employee Rate Modal */}
      <AddEmployeeRateModal
        open={isModalOpen}
        onClose={handleModalClose}
        rateId={editingRateId}
      />
    </div>
  );
};

export default EmployeeRatesPage;
